import { useQueryClient } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  CreateWalletToPspWithdrawTransaction,
  denormalizeCreateWalletToPspWithdrawTransaction,
} from '@model/CreateWalletToPspWithdrawTransaction'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'

import { useFundingNav } from '@hooks/useFundingNav'

// import { useWithdrawalTracker } from '@analytics/amplitude/withdrawal'

import { buildInlineWithdrawalBankAccountPayload } from '../../WithdrawalFlowLayout/helpers'
import type { WithdrawalFormValues } from '../../WithdrawalFlowLayout/types'

const buildWithdrawPayload = (
  values: WithdrawalFormValues
): CreateWalletToPspWithdrawTransaction => ({
  fromWallet: {
    id: values.wallet.id,
    currency: { id: values.wallet.currency.id },
  },
  toPaymentProvider: {
    id: values.paymentProvider.id,
    currency: { id: values.paymentProvider.currency.id },
  },
  amount: values.amount,
  comment: values.comment,
  terms: values.terms.paymentOperations,
  fields: values.fields,
  form: values.paymentProvider.parameters.form ?? [],
})

export const useConfirmWithdrawal = () => {
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { go } = useFundingNav()
  // const tracker = useWithdrawalTracker()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { values, validateForm, setSubmitting, isSubmitting } =
    useFormikContext<WithdrawalFormValues>()

  useEffect(() => {
    setSubmitting(false)
  }, [setSubmitting])

  const handleConfirm = useCallback(async () => {
    setSubmitError(null)

    const errors = await validateForm()
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)

    try {
      const isBankProvider = isPaymentProviderBankType(values.paymentProvider?.providerCategory?.id)

      let clientBankAccountDetailsId = values.bankAccount?.id

      if (isBankProvider && !clientBankAccountDetailsId) {
        const created = await apiClient.addBankAccount(
          buildInlineWithdrawalBankAccountPayload(values)
        )
        clientBankAccountDetailsId = created.id
        queryClient.invalidateQueries({ queryKey: ['profile'] })
      }

      const payload = denormalizeCreateWalletToPspWithdrawTransaction(
        buildWithdrawPayload(values),
        locale,
        clientBankAccountDetailsId
      )

      const tx = await apiClient.transactionWithdraw(payload)

      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transaction'] })

      const requiresTwoFactor = (tx.twoFactor?.length ?? 0) > 0

      if (requiresTwoFactor) {
        go('../two-factor', { state: { transaction: tx, withdrawalPayload: payload } })
        return
      }

      // tracker.submitted({ withdrawal_id: tx.id })
      go('../transaction/success', { state: { transaction: tx } })
    } catch {
      const errorMessage = t('Something went wrong! Please try again later')
      setSubmitError(errorMessage)
      // tracker.failed({ message: errorMessage })
      go('../transaction/failed', { replace: true })
    } finally {
      setSubmitting(false)
    }
  }, [apiClient, go, locale, queryClient, setSubmitting, t, validateForm, values])

  return { handleConfirm, submitError, isSubmitting }
}
